import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

// PKCE Helper Functions
const base64URLEncode = (buffer: Uint8Array): string => {
  return btoa(String.fromCharCode(...buffer))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

const generateCodeVerifier = (): string => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64URLEncode(array);
};

const generateCodeChallenge = async (verifier: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return base64URLEncode(new Uint8Array(hash));
};

export const AuthStartMicrosoft = () => {
  const [error, setError] = useState<string | null>(null);
  const [showFallback, setShowFallback] = useState(false);
  const startedRef = useRef(false);

  const initPKCEFlow = async () => {
    try {
      setError(null);

      const clientId = '5eaa225e-61a5-407a-93b0-20d0eb819191';

      const urlParams = new URLSearchParams(window.location.search);
      const dialogSessionId = urlParams.get('sessionId');
      const mode = urlParams.get('mode'); // 'dialog' | 'browser' | null

      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);

      const storageKey = 'pkce_verifier_direct_login';
      try {
        sessionStorage.setItem(storageKey, codeVerifier);
      } catch {}
      try {
        localStorage.setItem(storageKey, codeVerifier);
      } catch {}

      const redirectUri = encodeURIComponent(`${window.location.origin}/auth-callback-microsoft`);
      const scopes = encodeURIComponent('openid profile User.Read Contacts.ReadWrite MailboxSettings.ReadWrite');

      // Encode both mode and sessionId in state so the callback can detect browser-handoff
      // even if Microsoft strips extra query parameters.
      let stateParam = '';
      if (dialogSessionId || mode) {
        const statePayload = {
          m: mode || '',
          s: dialogSessionId || '',
        };
        stateParam = encodeURIComponent(btoa(JSON.stringify(statePayload)));
      }

      const authUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?` +
        `client_id=${clientId}` +
        `&response_type=code` +
        `&redirect_uri=${redirectUri}` +
        `&scope=${scopes}` +
        `&response_mode=query` +
        `&code_challenge=${codeChallenge}` +
        `&code_challenge_method=S256` +
        (stateParam ? `&state=${stateParam}` : '');

      window.location.href = authUrl;
    } catch (err) {
      setError('Failed to initialize authentication. Please try again.');
    }
  };

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    initPKCEFlow();

    const fallbackTimer = setTimeout(() => {
      setShowFallback(true);
    }, 4000);

    return () => clearTimeout(fallbackTimer);
  }, []);

  const handleRetry = () => {
    setShowFallback(false);
    initPKCEFlow();
  };

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center space-y-4 p-6">
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="default" onClick={handleRetry}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <div className="text-center space-y-4 p-6 max-w-sm">
        <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4 text-primary" />
        <p className="text-sm text-muted-foreground">Redirecting to Microsoft login…</p>

        {showFallback && (
          <div className="pt-4 space-y-3">
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-xs text-muted-foreground">
                Still here? Click below to continue to Microsoft.
              </p>
            </div>
            <Button variant="default" size="lg" onClick={handleRetry}>
              <svg className="mr-2 h-4 w-4" viewBox="0 0 21 21">
                <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
              </svg>
              Continue to Microsoft
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
